import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { ServiceRequest } from "../types";
import { setupRealtimeSubscription } from "../utils/realtimeUtils";
import { 
  fetchAllRequests, 
  fetchRequestById, 
  updateRequestSchedule, 
  acceptRequest, 
  rejectRequest, 
  completeRequest,
  markRequestAsPaid 
} from "../utils/requestUtils";
import { supabase } from "@/integrations/supabase/client";
import { triggerServiceWebhook, sendServiceReminder, triggerDailySummaryTest } from "@/utils/webhookUtils";
import { parseISO, isBefore } from 'date-fns';

export const useServiceRequests = (currentUserId?: string | null) => {
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const [userId, setUserId] = useState<string | null>(currentUserId || null);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (!currentUserId) {
        try {
          const { data } = await supabase.auth.getUser();
          if (data.user) {
            setUserId(data.user.id);
          }
        } catch (error) {
          console.error('Error fetching current user:', error);
        }
      }
    };
    
    fetchCurrentUser();
  }, [currentUserId]);

  const fetchRequests = async () => {
    if (!userId) return;
    
    try {
      const data = await fetchAllRequests(userId);
      console.log('Fetched service requests:', data);
      setRequests(data);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch service requests",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchNewRequest = async (requestId: string) => {
    if (!userId) return;
    
    try {
      const data = await fetchRequestById(requestId, userId);

      if (data) {
        console.log('New request data fetched:', data);
        
        let status = data.status;
        let autoApproved = false;
        
        if (data.status === 'pending' && data.services.instant_booking) {
          await acceptRequest(requestId);
          status = 'accepted';
          autoApproved = true;
        }
        
        const updatedRequest = {
          ...data,
          status: status as 'pending' | 'accepted' | 'rejected' | 'completed'
        };
        
        setRequests(prevRequests => [updatedRequest, ...prevRequests]);
        
        toast({
          title: autoApproved ? "New Service Request (Auto-Approved)" : "New Service Request",
          description: `${data.customer_name} requested ${data.services.name}`,
        });

        try {
          console.log('Preparing to send notification for new request:', data);
          
          const webhookData = {
            serviceName: data.services.name,
            servicePrice: data.services.price.toString(),
            serviceStatus: status,
            customerName: data.customer_name,
            customerPhone: data.customer_phone || "Not provided",
            customerEmail: data.customer_email || "Not provided",
            appointmentDate: data.scheduled_at,
            formattedAppointmentDate: data.scheduled_at ? new Date(data.scheduled_at).toLocaleString() : "Not scheduled",
            specialRequests: data.notes || "None",
            location: data.location || "Not specified",
            requestId: data.id,
            autoApproved: autoApproved
          };
          
          await triggerServiceWebhook('service_request', webhookData, userId);
          console.log('Notification sent successfully');
          
          if (autoApproved) {
            await triggerServiceWebhook('service_accepted', webhookData, userId);
            console.log('Auto-approval notification sent successfully');
          }
        } catch (notifyError) {
          console.error('Failed to send notification:', notifyError);
        }
      }
    } catch (error) {
      console.error('Error fetching new request:', error);
    }
  };

  const handleAcceptRequest = async (requestId: string) => {
    if (!userId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "User not authenticated",
      });
      return false;
    }
    
    try {
      await acceptRequest(requestId);
      
      const updatedRequests = requests.map(request => 
        request.id === requestId 
          ? { ...request, status: 'accepted' as const } 
          : request
      );
      
      setRequests(updatedRequests);
      
      toast({
        title: "Success",
        description: "Service request accepted",
      });
      
      const acceptedRequest = await fetchRequestById(requestId, userId);
      
      if (acceptedRequest) {
        try {
          console.log('Preparing to send notification for accepted request:', acceptedRequest);
          
          const webhookData = {
            serviceName: acceptedRequest.services.name,
            servicePrice: acceptedRequest.services.price.toLocaleString(),
            serviceStatus: 'accepted',
            customerName: acceptedRequest.customer_name,
            customerPhone: acceptedRequest.customer_phone || "Not provided",
            customerEmail: acceptedRequest.customer_email || "Not provided",
            appointmentDate: acceptedRequest.scheduled_at,
            formattedAppointmentDate: acceptedRequest.scheduled_at 
              ? new Date(acceptedRequest.scheduled_at).toLocaleString() 
              : "Not scheduled",
            specialRequests: acceptedRequest.notes || "None",
            location: acceptedRequest.location || "Not specified",
            requestId: acceptedRequest.id
          };
          
          await triggerServiceWebhook('service_accepted', webhookData, userId);
          console.log('Acceptance notification sent successfully');
        } catch (notifyError) {
          console.error('Failed to send acceptance notification:', notifyError);
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error accepting service request:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to accept the request",
      });
      return false;
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    if (!userId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "User not authenticated",
      });
      return false;
    }
    
    try {
      await rejectRequest(requestId);
      
      setRequests(prevRequests => 
        prevRequests.map(request => 
          request.id === requestId 
            ? { ...request, status: 'rejected' as const } 
            : request
        )
      );
      
      toast({
        title: "Success",
        description: "Service request rejected",
      });
      
      return true;
    } catch (error) {
      console.error('Error rejecting service request:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to reject the request",
      });
      return false;
    }
  };

  const handleCompleteRequest = async (requestId: string) => {
    if (!userId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "User not authenticated",
      });
      return false;
    }
    
    const requestToComplete = requests.find(req => req.id === requestId);
    
    if (!requestToComplete) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Request not found",
      });
      return false;
    }
    
    if (requestToComplete.scheduled_at) {
      const scheduledTime = parseISO(requestToComplete.scheduled_at);
      const now = new Date();
      
      if (!isBefore(scheduledTime, now)) {
        toast({
          variant: "destructive",
          title: "Cannot Complete Yet",
          description: "A service cannot be marked as complete before its start time",
        });
        return false;
      }
    }
    
    try {
      await completeRequest(requestId);
      
      setRequests(prevRequests => 
        prevRequests.map(request => 
          request.id === requestId 
            ? { ...request, status: 'completed' as const } 
            : request
        )
      );
      
      toast({
        title: "Success",
        description: "Service request marked as completed",
      });
      
      return true;
    } catch (error) {
      console.error('Error completing service request:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to complete the request",
      });
      return false;
    }
  };

  const handleMarkAsPaid = async (requestId: string) => {
    if (!userId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "User not authenticated",
      });
      return false;
    }
    
    try {
      await markRequestAsPaid(requestId);
      
      setRequests(prevRequests => 
        prevRequests.map(request => 
          request.id === requestId 
            ? { ...request, paid: true } 
            : request
        )
      );
      
      toast({
        title: "Success",
        description: "Payment status updated successfully",
      });
      
      return true;
    } catch (error) {
      console.error('Error marking request as paid:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update payment status",
      });
      return false;
    }
  };

  const sendReminderToCustomer = async (requestId: string): Promise<{ success: boolean, error?: string }> => {
    if (!userId) return { success: false, error: "User not authenticated" };
    return await sendServiceReminder(requestId, userId);
  };

  const rescheduleServiceRequest = async (requestId: string, newDate: Date) => {
    if (!userId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "User not authenticated",
      });
      return false;
    }
    
    try {
      await updateRequestSchedule(requestId, newDate);
      
      setRequests(prevRequests => 
        prevRequests.map(request => 
          request.id === requestId 
            ? { ...request, scheduled_at: newDate.toISOString(), status: 'accepted' as const } 
            : request
        )
      );
      
      toast({
        title: "Success",
        description: "Appointment rescheduled successfully",
      });
      
      const updatedRequest = await fetchRequestById(requestId, userId);
      
      if (updatedRequest) {
        try {
          const webhookData = {
            serviceName: updatedRequest.services.name,
            servicePrice: updatedRequest.services.price.toLocaleString(),
            serviceStatus: updatedRequest.status,
            customerName: updatedRequest.customer_name,
            customerPhone: updatedRequest.customer_phone || "Not provided",
            customerEmail: updatedRequest.customer_email || "Not provided",
            appointmentDate: updatedRequest.scheduled_at,
            formattedAppointmentDate: updatedRequest.scheduled_at 
              ? new Date(updatedRequest.scheduled_at).toLocaleString() 
              : "Not scheduled",
            specialRequests: updatedRequest.notes || "None",
            location: updatedRequest.location || "Not specified",
            requestId: updatedRequest.id
          };
          
          await triggerServiceWebhook('service_reschedule', webhookData, userId);
        } catch (notifyError) {
          console.error('Failed to send reschedule notification:', notifyError);
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error rescheduling service request:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to reschedule the appointment",
      });
      return false;
    }
  };

  const testDailySummary = async () => {
    if (!userId) return { success: false, error: "User not authenticated" };
    return await triggerDailySummaryTest(userId);
  };

  useEffect(() => {
    if (userId) {
      fetchRequests();
      const cleanup = setupRealtimeSubscription(userId, fetchNewRequest);
      return cleanup;
    }
  }, [userId]);

  return {
    requests,
    loading,
    fetchRequests,
    sendReminderToCustomer,
    rescheduleServiceRequest,
    testDailySummary,
    handleAcceptRequest,
    handleRejectRequest,
    handleCompleteRequest,
    handleMarkAsPaid
  };
};
